import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isSameDay } from 'date-fns';
import { isEqual } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Button, Stack } from '@mui/material';

import StatusIcon from '../../assets/icons/StatusIconInFilters.svg';
import FreeTextSearchFilter, { SEARCHBAR_BACKGROUND } from '../../components/FreeTextSearchFilter';
import LabeledField from '../../components/LabeledField';
import getStepData from '../../components/RemainingActionsCounterCard/getStepData';
import RemainingActionsCounterCard from '../../components/RemainingActionsCounterCard/RemainingActionsCounterCard';
import { SearchableSelectDropdownFilter } from '../../components/SearchableSelectDropdownFilter/SearchableSelectDropdownFilter';
import { SelectDropdownFilter } from '../../components/SelectDropdownFilter/SelectDropdownFilter';
import TimekeepingOverviewCard from '../../components/TimekeepingOverviewCard/TimekeepingOverviewCard';
import selectUser from '../../store/selectors/appSelector';
import {
  selectIsDisputedTimecardUpdating,
  selectIsOverviewDataReload,
  selectIsReloadTimecards,
  selectIsTimecardsLoading,
  selectIsTimekeepingOverviewDataLoading,
  selectTimecards,
  selectTimekeepingOverviewData,
  selectTotalTimecardsCount,
} from '../../store/selectors/timekeepingSelector';
import { PRIMARY_FONT } from '../../theme/fontConstants';

import BatchProcessModalByFilter from './components/BatchProcessModal/BatchProcessModalByFilter';
import BatchProcessModalById from './components/BatchProcessModal/BatchProcessModalById';
import ExportView from './components/Exports/ExportViewWrapper';
import TimekeepingBulkButtons from './components/TimekeepingBulkButtons';
import TimekeepingDayWeekFilter from './components/TimekeepingDayWeekFilter';
import WorkerTimecardSet from './components/WorkerTimecardSet';
import {
  BULK_ACTION_TYPES,
  EXPORT_TYPES,
  TIMEKEEPING_STATUSES,
  TIMEKEEPING_TAGS,
} from './timekeepingConstants';
import {
  extractPlacementIdsFromUserMap,
  getEndOfDayInEpoch,
  getStartOfDayInEpoch,
  getTotalHourCountFromSelectedTimecards,
} from './timekeepingHelpers';
import { getTimecards, getTimekeepingOverviewData } from './timekeepingReducer';

const corporationsAPICallback = (corpId) => ({
  httpMethod: 'POST',
  route: 'corporations/read',
  generateBody: (searchTerm) => {
    const filters = corpId
      ? [{ operation: 'equalsID', field: '_id', value: corpId }]
      : [{ operation: 'icontains', field: 'name', value: searchTerm }];
    return {
      filters,
    };
  },
});

const employeeAPICallback = (employeeId) => ({
  httpMethod: 'POST',
  route: 'users/read',
  generateBody: (searchTerm) => {
    const filters = employeeId
      ? [{ operation: 'equalsID', field: '_id', value: employeeId }]
      : [
          { operation: 'icontains', field: 'name', value: searchTerm },
          { operation: 'equals', field: 'role', value: 'candidate' },
        ].filter((i) => i);
    return {
      filters,
    };
  },
});

const departmentAPICallback = (corpId, departmentId) => ({
  httpMethod: 'POST',
  route: 'corporations/departments/read',
  generateBody: (searchTerm) => {
    const filters = departmentId
      ? [{ operation: 'equalsID', field: '_id', value: departmentId }]
      : [searchTerm && { operation: 'icontains', field: 'name', value: searchTerm }].filter(
          (i) => i
        );
    return {
      ...(corpId && { corporation: corpId }),
      filters,
    };
  },
});

const exportButtonStyle = {
  width: '125px',
  height: '36px',
  fontFamily: PRIMARY_FONT[500],
  textTransform: 'none',
};

const locationsAPICallback = (corpId, locationId) => ({
  httpMethod: 'POST',
  route: 'locations/read',
  generateBody: (searchTerm) => {
    const filters = locationId
      ? [{ operation: 'equalsID', field: '_id', value: locationId }]
      : [
          searchTerm && { operation: 'icontains', field: 'address', value: searchTerm },
          corpId && {
            operation: 'equalsID',
            field: 'corporation._id',
            value: corpId,
          },
        ].filter((i) => i);
    return {
      filters,
    };
  },
});

const parsedUrlFilters = (searchParams) => {
  const initFilters = [];
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  if (startDate && endDate) {
    initFilters.push(
      { field: 'start', value: parseInt(startDate, 10), operation: 'onOrAfter' },
      { field: 'start', value: parseInt(endDate, 10), operation: 'onOrBefore' }
    );
  }

  const status = searchParams.get('status');
  if (status) {
    initFilters.push({
      operation: 'includedIn',
      field: 'timecard.status',
      value: status.split(','),
    });
  }

  const tags = searchParams.get('tags');
  if (tags) {
    initFilters.push({ value: tags.split(','), field: 'timecard.tag', operation: 'includedIn' });
  }

  const corporation = searchParams.get('corporation');
  if (corporation) {
    initFilters.push({ value: corporation, field: 'corporation', operation: 'equalsID' });
  }
  const employee = searchParams.get('employee');
  if (employee) {
    initFilters.push({ value: employee, field: 'candidate', operation: 'equalsID' });
  }

  const department = searchParams.get('department');
  if (department) {
    initFilters.push({
      value: department,
      field: 'jobOrder.department._id',
      operation: 'equalsID',
    });
  }

  const location = searchParams.get('location');
  if (location) {
    initFilters.push({ value: location, field: 'jobOrder.location', operation: 'equalsID' });
  }

  const position = searchParams.get('position');
  if (position) {
    initFilters.push({ value: position, field: 'jobOrder.name', operation: 'icontains' });
  }
  return initFilters;
};

const INITIAL_FILTERS = {
  page: 0,
  pageSize: 5,
};

const Timekeeping = () => {
  const statusOptions = [
    {
      value: TIMEKEEPING_STATUSES.PENDING,
      name: LanguageConverterV2('timekeeping.status.pending'),
    },
    {
      value: TIMEKEEPING_STATUSES.RESOLVED,
      name: LanguageConverterV2('timekeeping.status.resolved'),
    },
    {
      value: TIMEKEEPING_STATUSES.DISPUTED,
      name: LanguageConverterV2('timekeeping.status.disputed'),
    },
    {
      value: TIMEKEEPING_STATUSES.APPROVED,
      name: LanguageConverterV2('timekeeping.status.approved'),
    },
    {
      value: TIMEKEEPING_STATUSES.FINALIZED,
      name: LanguageConverterV2('timekeeping.status.finalized'),
    },
  ];

  const tagOptions = [
    {
      value: TIMEKEEPING_TAGS.DISPUTED,
      name: LanguageConverterV2('timekeeping.daily.tags.disputed'),
    },
    {
      value: TIMEKEEPING_TAGS.MISSING_PUNCH,
      name: LanguageConverterV2('timekeeping.daily.tags.missingPunches'),
    },
    {
      value: TIMEKEEPING_TAGS.LOOKS_GOOD,
      name: LanguageConverterV2('timekeeping.daily.tags.looksGood'),
    },
    {
      value: TIMEKEEPING_TAGS.SUSPICIOUS,
      name: LanguageConverterV2('timekeeping.daily.tags.suspicious'),
    },
  ];

  const formatEpochAsHours = (epoch) => {
    if (epoch && typeof epoch === 'number') {
      return (epoch / 60 / 60 / 1000).toFixed(2);
    }
    return null;
  };

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousFiltersRef = useRef();

  const [isBulkActionModalOpen, setIsBulkActionModalOpen] = useState(undefined);
  const [isBulkIdModalOpen, setIsBulkIdModalOpen] = useState(undefined);
  const [activeExport, setActiveExport] = useState(null);

  const candidateTimecards = useSelector(selectTimecards);
  const total = useSelector(selectTotalTimecardsCount);
  const isDisputedTimecardUpdating = useSelector(selectIsDisputedTimecardUpdating);
  const isTimecardsLoading = useSelector(selectIsTimecardsLoading);
  const isReloadTimecards = useSelector(selectIsReloadTimecards);
  const timekeepingOverviewData = useSelector(selectTimekeepingOverviewData);
  const isTimekeepingOverviewDataLoading = useSelector(selectIsTimekeepingOverviewDataLoading);

  const [candidateCheckedMap, setCandidateCheckedMap] = useState({});
  const user = useSelector(selectUser);
  const isOverviewDataReload = useSelector(selectIsOverviewDataReload);

  const isUserAdminOrRecruiter = user?.role === UserRole.ADMIN || user?.role === UserRole.RECRUITER;

  const overviewTimecardCountReadyToApproveOrResolve =
    timekeepingOverviewData?.timecardCounts?.readyToApproveOrResolve;
  const overviewTimecardCountReadyToFinalize =
    timekeepingOverviewData?.timecardCounts?.readyToFinalize;

  const overviewTimecardApproveOrResolveHoursTotal = formatEpochAsHours(
    (isUserAdminOrRecruiter &&
      timekeepingOverviewData?.workedTimeEpoch?.readyToApproveOrResolveEpoch) ||
      timekeepingOverviewData?.workedTimeEpoch?.corporation?.readyToApproveOrResolveEpoch
  );

  const selectedStatuses = (searchParams.get('status') || '').split(',').filter(Boolean);

  const initialStatus =
    selectedStatuses?.length > 0
      ? statusOptions.filter((st) => selectedStatuses.includes(st.value))
      : [];
  const selectedTags = (searchParams.get('tags') || '').split(',').filter(Boolean);
  const initialTags =
    selectedTags?.length > 0 ? tagOptions.filter((tag) => selectedTags.includes(tag.value)) : [];
  const selectedEmployeeId = searchParams.get('employee');
  const selectedCorporationId =
    user?.role === UserRole.EMPLOYER
      ? user?.employer?.corporation
      : searchParams.get('corporation');
  const selectedDepartmentId = searchParams.get('department');
  const selectedLocationId = searchParams.get('location');
  const currentPage = parseInt(searchParams.get('page'), 10) || 0;

  const [filterAndSortData, setFilterAndSortData] = useState(() => {
    const initFilters = parsedUrlFilters(searchParams);
    return {
      ...INITIAL_FILTERS,
      page: currentPage,
      filters: initFilters,
    };
  });

  const currentStart = searchParams.get('startDate');
  const startEpoch = parseInt(currentStart, 10);
  const currentEnd = searchParams.get('endDate');
  const endEpoch = parseInt(currentEnd, 10);
  const isDaily = isSameDay(startEpoch, endEpoch);

  const handleOnWeeklyDailyFilterChange = useCallback(
    (val) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      newParams.startDate = val.startDate;
      newParams.endDate = val?.endDate;
      setCandidateCheckedMap({});
      setSearchParams(newParams);
    },
    [setSearchParams, searchParams]
  );

  const handleStatusChange = useCallback(
    (newStatus) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (newStatus.length === 0) {
        delete newParams.status;
      } else {
        newParams.status = newStatus.join(',');
      }

      if (newParams.status !== searchParams.get('status')) {
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleTagsChange = useCallback(
    (newTags) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (newTags.length === 0) {
        delete newParams.tags;
      } else {
        newParams.tags = newTags.join(',');
      }

      if (newParams.tags !== searchParams.get('tags')) {
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleEmployeeSearchChange = useCallback(
    (employeeId) => {
      if (employeeId !== searchParams.get('employee')) {
        const newParams = { ...Object.fromEntries(searchParams) };
        if (employeeId) {
          newParams.employee = employeeId;
        } else {
          delete newParams.employee;
        }
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleCorporationChange = useCallback(
    (corporationId) => {
      if (corporationId !== searchParams.get('corporation')) {
        const newParams = { ...Object.fromEntries(searchParams) };
        if (corporationId) {
          newParams.corporation = corporationId;
        } else {
          delete newParams.corporation;
        }
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleDepartmentChange = useCallback(
    (department) => {
      if (department !== searchParams.get('department')) {
        const newParams = { ...Object.fromEntries(searchParams) };
        if (department) {
          newParams.department = department;
        } else {
          delete newParams.department;
        }
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleLocationChange = useCallback(
    (location) => {
      if (location !== searchParams.get('location')) {
        const newParams = { ...Object.fromEntries(searchParams) };
        if (location) {
          newParams.location = location;
        } else {
          delete newParams.location;
        }
        setCandidateCheckedMap({});
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );
  const handlePositionSearchChange = useCallback(
    (newPositionFilter) => {
      const positionSearchTerm = newPositionFilter?.[0]?.value;

      const newParams = { ...Object.fromEntries(searchParams) };
      if (!positionSearchTerm) {
        delete newParams.position;
      } else {
        newParams.position = positionSearchTerm;
      }
      setCandidateCheckedMap({});
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const startDateFilter = filterAndSortData.filters?.filter((f) => f.field === 'start');
    if (!isTimecardsLoading && startDateFilter?.length === 2) {
      const payload = {
        filters: filterAndSortData.filters,
        limit: filterAndSortData.pageSize,
        page: filterAndSortData.page,
      };
      if (!isEqual(previousFiltersRef.current, payload) || isReloadTimecards) {
        previousFiltersRef.current = payload;
        dispatch(getTimecards(payload));
      }
    }
  }, [dispatch, isTimecardsLoading, filterAndSortData, isReloadTimecards]);

  useEffect(() => {
    const searchParamsStartDate = searchParams.get('startDate');
    const searchParamsEndDate = searchParams.get('endDate');
    if (searchParamsStartDate && searchParamsEndDate) {
      dispatch(
        getTimekeepingOverviewData({
          start: parseInt(searchParamsStartDate, 10),
          end: parseInt(searchParamsEndDate, 10),
        })
      );
    }
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (isOverviewDataReload) {
      const searchParamsStartDate = searchParams.get('startDate');
      const searchParamsEndDate = searchParams.get('endDate');
      if (searchParamsStartDate && searchParamsEndDate) {
        dispatch(
          getTimekeepingOverviewData({
            start: parseInt(searchParamsStartDate, 10),
            end: parseInt(searchParamsEndDate, 10),
          })
        );
      }
    }
  }, [isOverviewDataReload, dispatch, searchParams]);

  useEffect(() => {
    if (!searchParams.get('startDate') || !searchParams.get('endDate')) {
      setCandidateCheckedMap({});
      setSearchParams({
        startDate: getStartOfDayInEpoch(Date.now()),
        endDate: getEndOfDayInEpoch(Date.now()),
      });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setFilterAndSortData((prev) => {
      const filters = parsedUrlFilters(searchParams);

      if (prev.page !== currentPage) {
        return { ...prev, filters, page: currentPage };
      }

      if (isEqual(prev.filters, filters)) {
        return prev;
      }

      if (filters.length > 0) {
        const newParams = { ...Object.fromEntries(searchParams) };
        newParams.page = 0;
        setCandidateCheckedMap({});
        setSearchParams(newParams);
        return { ...prev, filters, page: 0 };
      }
      return { ...INITIAL_FILTERS };
    });
  }, [currentPage, searchParams, setSearchParams]);

  const handleOnCardClick = useCallback(
    ({ filterVal, actionType }) => {
      if (actionType === BULK_ACTION_TYPES.RESOLVE) {
        setIsBulkActionModalOpen(BULK_ACTION_TYPES.RESOLVE);
      } else if (actionType === BULK_ACTION_TYPES.APPROVE) {
        setIsBulkActionModalOpen(BULK_ACTION_TYPES.APPROVE);
      }

      setCandidateCheckedMap({});
      setSearchParams(filterVal);
    },
    [setIsBulkActionModalOpen, setSearchParams]
  );

  const stepData = getStepData({
    timecardCounts: timekeepingOverviewData?.timecardCounts,
    role: user.role,
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
    handleOnCardClick,
    currentParams: { ...Object.fromEntries(searchParams) },
    workedTimeEpochs: timekeepingOverviewData?.workedTimeEpoch,
  });

  const handlePageChange = useCallback(
    (page) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      newParams.page = page;
      setCandidateCheckedMap({});
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const itemsToActOn = extractPlacementIdsFromUserMap(candidateCheckedMap);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px' }}>
      {isBulkActionModalOpen && (
        <BatchProcessModalByFilter
          // Timecard count is set to the amount that need to be approve/resolved or finalized depending on which modal is being used.
          initialTotalTimecardCount={
            ((isBulkActionModalOpen === BULK_ACTION_TYPES.APPROVE ||
              isBulkActionModalOpen === BULK_ACTION_TYPES.RESOLVE) &&
              overviewTimecardCountReadyToApproveOrResolve) ||
            (isBulkActionModalOpen === BULK_ACTION_TYPES.FINALIZE &&
              overviewTimecardCountReadyToFinalize)
          }
          // TODO: API does not yet support finalized hours total, but it will be needed later.
          initialTotalHourCount={overviewTimecardApproveOrResolveHoursTotal}
          isOpen={isBulkActionModalOpen}
          onClose={() => {
            setIsBulkActionModalOpen(undefined);
            dispatch(
              getTimekeepingOverviewData({
                start: parseInt(searchParams.get('startDate'), 10),
                end: parseInt(searchParams.get('endDate'), 10),
              })
            );

            handlePageChange(0);
            const payload = {
              filters: filterAndSortData.filters,
              limit: filterAndSortData.pageSize,
              page: 0,
            };
            dispatch(getTimecards(payload));
          }}
          onGoBack={() => {
            setIsBulkActionModalOpen(undefined);
          }}
          start={parseInt(searchParams.get('startDate'), 10)}
          end={parseInt(searchParams.get('endDate'), 10)}
        />
      )}

      {isBulkIdModalOpen && (
        <BatchProcessModalById
          initialTotalHourCount={getTotalHourCountFromSelectedTimecards(
            itemsToActOn,
            candidateTimecards
          )}
          isOpen={isBulkIdModalOpen}
          onClose={() => {
            setIsBulkIdModalOpen(undefined);
            dispatch(
              getTimekeepingOverviewData({
                start: parseInt(searchParams.get('startDate'), 10),
                end: parseInt(searchParams.get('endDate'), 10),
              })
            );
            handlePageChange(0);
            const payload = {
              filters: filterAndSortData.filters,
              limit: filterAndSortData.pageSize,
              page: 0,
            };
            dispatch(getTimecards(payload));
            setCandidateCheckedMap({});
          }}
          onGoBack={() => {
            setIsBulkIdModalOpen(undefined);
          }}
        />
      )}
      {activeExport && !isDaily && (
        <ExportView
          filters={[
            { field: 'start', value: startEpoch, operation: 'onOrAfter' },
            { field: 'start', value: endEpoch, operation: 'before' },
          ]}
          user={user}
          activeExport={activeExport}
          onClose={() => setActiveExport(null)}
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <TimekeepingDayWeekFilter
          startEpoch={startEpoch}
          endEpoch={endEpoch}
          isDaily={isDaily}
          handleOnWeeklyDailyFilterChange={handleOnWeeklyDailyFilterChange}
        />
        {!isDaily && user?.role === UserRole.ADMIN && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              sx={exportButtonStyle}
              onClick={() => {
                setActiveExport(EXPORT_TYPES.PAYROLL);
              }}
              data-testid="remaining-actions-action-button"
            >
              {LanguageConverterV2('button.export.payroll')}
            </Button>
            <Button
              variant="outlined"
              sx={exportButtonStyle}
              onClick={() => {
                setActiveExport(EXPORT_TYPES.BILLING);
              }}
              data-testid="remaining-actions-action-button"
            >
              {LanguageConverterV2('button.export.billing')}
            </Button>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TimekeepingOverviewCard
          isLoading={isTimekeepingOverviewDataLoading}
          titleText={
            isDaily
              ? LanguageConverterV2('timekeeping.overviewCard.dailyOverview')
              : LanguageConverterV2('timekeeping.overviewCard.weeklyOverview')
          }
          numOfTimeCards={timekeepingOverviewData?.timecardCounts?.total}
          regularHours={timekeepingOverviewData?.workedTimeEpoch?.regular}
          overTimeHours={timekeepingOverviewData?.workedTimeEpoch?.overtime}
          totalHours={timekeepingOverviewData?.workedTimeEpoch?.total}
        />
        {stepData?.map((step) => (
          <RemainingActionsCounterCard
            isCompleted={step?.isCompleted}
            isLoading={isTimekeepingOverviewDataLoading}
            isSelected={step?.isSelected}
            onCardClick={step?.onCardClick}
            header={step?.header}
            subHeader={step?.subHeader}
            body={step?.body}
            count={step?.count}
            color={step?.color}
            buttonText={step?.buttonText}
            key={step.header}
          />
        ))}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Stack gap="12px" mb="24px">
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
            <Box>
              <LabeledField
                title={LanguageConverterV2('timekeeping.filter.status')}
                field={
                  <SelectDropdownFilter
                    disabled={isTimecardsLoading}
                    placeholder={LanguageConverterV2('timekeeping.filter.status')}
                    isMultiSelectable
                    onValueChange={handleStatusChange}
                    options={statusOptions}
                    getOptionLabel={(option) => option.name}
                    wrapperSx={{ width: '350px' }}
                    startAdornmentIcon={
                      <Box
                        component="img"
                        sx={{ height: 16, width: 16 }}
                        alt="Status"
                        src={StatusIcon}
                      />
                    }
                    initialValue={initialStatus}
                  />
                }
              />
            </Box>
            <Box>
              <LabeledField
                title={LanguageConverterV2('timekeeping.filter.tags')}
                field={
                  <SelectDropdownFilter
                    disabled={isTimecardsLoading}
                    isMultiSelectable
                    placeholder={LanguageConverterV2('timekeeping.filter.tags')}
                    onValueChange={handleTagsChange}
                    options={tagOptions}
                    getOptionLabel={(option) => option.name}
                    startAdornmentIcon={
                      <Box
                        component="img"
                        sx={{ height: 16, width: 16 }}
                        alt="Status"
                        src={StatusIcon}
                      />
                    }
                    wrapperSx={{ width: '350px' }}
                    initialValue={initialTags}
                  />
                }
              />
            </Box>
          </Box>

          <Box display="flex" flexDirection="row" gap="12px">
            <LabeledField
              title={LanguageConverterV2('timekeeping.filter.employeeName')}
              field={
                <SearchableSelectDropdownFilter
                  disabled={isTimecardsLoading}
                  placeholder={LanguageConverterV2('timekeeping.filter.employeeName')}
                  onValueChange={handleEmployeeSearchChange}
                  optionsAPICallback={employeeAPICallback(selectedEmployeeId)}
                  getOptionLabel={(option) => option.name}
                  background={SEARCHBAR_BACKGROUND.DEFAULT}
                  initialValue={selectedEmployeeId}
                />
              }
            />
            {isUserAdminOrRecruiter && (
              <LabeledField
                title={LanguageConverterV2('timekeeping.filter.corporation')}
                field={
                  <SearchableSelectDropdownFilter
                    disabled={isTimecardsLoading}
                    placeholder={LanguageConverterV2('timekeeping.filter.corporation')}
                    onValueChange={handleCorporationChange}
                    optionsAPICallback={corporationsAPICallback(selectedCorporationId)}
                    getOptionLabel={(option) => option.name}
                    background={SEARCHBAR_BACKGROUND.DEFAULT}
                    initialValue={searchParams.get('corporation')}
                  />
                }
              />
            )}
            <LabeledField
              title={LanguageConverterV2('timekeeping.filter.department')}
              field={
                <SearchableSelectDropdownFilter
                  disabled={isTimecardsLoading || !selectedCorporationId}
                  placeholder={LanguageConverterV2('timekeeping.filter.department')}
                  onValueChange={handleDepartmentChange}
                  optionsAPICallback={departmentAPICallback(
                    selectedCorporationId,
                    selectedDepartmentId
                  )}
                  getOptionLabel={(option) => option.name}
                  background={SEARCHBAR_BACKGROUND.DEFAULT}
                  initialValue={selectedDepartmentId}
                />
              }
            />
            <LabeledField
              title={LanguageConverterV2('timekeeping.filter.location')}
              field={
                <SearchableSelectDropdownFilter
                  disabled={isTimecardsLoading || !selectedCorporationId}
                  placeholder={LanguageConverterV2('timekeeping.filter.location')}
                  onValueChange={handleLocationChange}
                  optionsAPICallback={locationsAPICallback(
                    selectedCorporationId,
                    selectedLocationId
                  )}
                  getOptionLabel={(option) => `${option.address} - ${option.description}`}
                  background={SEARCHBAR_BACKGROUND.DEFAULT}
                  initialValue={selectedLocationId}
                />
              }
            />
            <LabeledField
              title={LanguageConverterV2('timekeeping.filter.positionName')}
              field={
                <FreeTextSearchFilter
                  placeholder={LanguageConverterV2('timekeeping.filter.positionName')}
                  onValueChange={handlePositionSearchChange}
                  field="name"
                  operation="icontains"
                  background={SEARCHBAR_BACKGROUND.DEFAULT}
                  initialValue={{ value: searchParams.get('position') }}
                  wrapperSx={{ opacity: isTimecardsLoading ? 0.5 : 1 }}
                />
              }
            />
          </Box>
        </Stack>

        <TimekeepingBulkButtons
          setBulkActionData={() => {}}
          userRole={user?.role}
          selectedPlacementIds={itemsToActOn}
          allTimecards={candidateTimecards}
          // TODO: It's probably not necessary that these are 3 separate props here for opening the different modal types.
          openFinalizeModal={() =>
            setIsBulkIdModalOpen({
              modalType: BULK_ACTION_TYPES.FINALIZE,
              placementIds: itemsToActOn,
            })
          }
          openResolveModal={() =>
            setIsBulkIdModalOpen({
              modalType: BULK_ACTION_TYPES.RESOLVE,
              placementIds: itemsToActOn,
            })
          }
          openApproveModal={() =>
            setIsBulkIdModalOpen({
              modalType: BULK_ACTION_TYPES.APPROVE,
              placementIds: itemsToActOn,
            })
          }
        />
      </Box>

      <WorkerTimecardSet
        candidateTimecards={candidateTimecards}
        total={total}
        isLoading={isTimecardsLoading}
        isDisputedTimecardUpdating={isDisputedTimecardUpdating}
        currentPage={filterAndSortData.page}
        pageSize={INITIAL_FILTERS.pageSize}
        onPageChange={handlePageChange}
        candidateCheckedMap={candidateCheckedMap}
        setCandidateCheckedMap={setCandidateCheckedMap}
      />
    </Box>
  );
};

export default Timekeeping;
